import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react'
import { connect, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { PageTitle } from 'Components/PageTitle'
import { Modal } from 'Components/Modal'
import { RosterViewWrapper } from 'Components/Campaigns/RosterView/RosterViewWrapper'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import { getDateOfISOWeek } from './../../../shared/libraries/utils/date'

import { updateMissionSimplified } from './../../../Module/graphQL'

import ImportMissions from './ImportMissions'
import { showReduxModal, hideReduxModal } from './../../../Reducers/UI/actions'
import {
  convertCreateMissionDtoToMission,
  validateCreateMissionDto,
  downloadSampleCSV,
  canImport,
  getRosterTitle,
  formatDataForComponentConsumption,
  getBreadcrumbs,
} from './util'
import { getDetailsItems } from '../../../Components/DetailsSection/utils'
import ImportButton from './components/ImportButton/ImportButton'
import DetailsSection from '../../../Components/DetailsSection/DetailsSection'
import BreadcrumbsRenderer from '../../../Components/BreadcrumbsRenderer/BreadcrumbsRenderer'
import RosterMissionsTable from '../../../Containers/Campaigns/RosterView/RosterMissionsTable/container/RosterMissionsTable'
import MissionsControls from '../../../Components/MissionsControls/MissionsControls'
import { CAMPAIGN_ENUM_TYPES, UNBOOK_REASONS } from '../../../shared/constants'
import ExportRoster from '../../../Components/ExportRoster'
import { apiClient } from '../../../Module/API/Client'
import { createMissions } from '../../../Module/graphQL'
import { SnackBarContext } from '../../../Components/SnackBar'
import { authorization } from '../../../Module/Auth/Auth'
import GenerateButton from './components/GenerateButton/GenerateButton'
import MissionsOverview from '../../../Components/MissionsOverview'
import Availabilities from '../../City/Page/Ambasadors/Availabilities'
import { handlingErrorContractAvailabilitiesPopup } from '../../City/Page/Ambasadors/Availabilities/utils'
import {
  isValidBonusPerConversion,
  isValidPercent,
} from '../../../Utils/Validators/numbers'
import {
  BONUS_PER_CONVERSION_ERROR,
  BONUS_THRESHOLD_ERROR,
} from '../../../constants'
import GenerateMissionsModal from './components/GenerateMissionsModal'
import ConfirmDialog from '../../../Components/ConfirmDialog/ConfirmDialog'
import { handleConfirmationDialog } from '../../City/Page/Ambasadors/EmailPipelineView/utils'

const countriesShowPayroll = ['BE', 'NL', 'DE']

const RosterView = props => {
  const { rosterId } = useParams()
  const [year, week] = rosterId.split('-')
  const startDate = getDateOfISOWeek(week, year)
  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + 7)

  const [state, setState] = useState({
    cityLocations: [],
    campaign: [],
    cityPlan: {},
    city: {},
    missions: [],
    countryCode: '',
  })
  const [editedMissionsData, setEditedMissionsData] = useState({})
  const [selectedMissionsMap, setSelectedMissionsMap] = useState({})
  const [isEditMode, setEditMode] = useState(false)
  const [availabilities, setAvailabilities] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showImportMissionsModal, setShowImportMissionsModal] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })

  const selectedMissionsMapRef = useRef(null)
  const runSnackBar = useContext(SnackBarContext)

  useEffect(() => {
    selectedMissionsMapRef.current = selectedMissionsMap
  }, [selectedMissionsMap])

  useEffect(() => {
    setState(
      formatDataForComponentConsumption({
        campaign: props.campaign,
        cityPlan: props.cityPlan,
        rosterId,
      }),
    )
  }, [])

  const callbackBrandAmbassadors = data => {
    setAvailabilities(data)
  }

  const {
    cityLocations,
    campaign,
    cityPlan,
    city,
    missions,
    countryCode,
  } = state

  const dispatch = useDispatch()

  const onMissionsImport = async (missionDtos, onProgress) => {
    const convertMissionDtoToMission = dto =>
      convertCreateMissionDtoToMission(dto, {
        locations: cityLocations,
      })

    const missions = missionDtos.map(d => ({
      ...convertMissionDtoToMission(d),
      // Add the city plan id
      cityPlanId: cityPlan.id,
    }))
    const allMissions = await createMissions({
      missions,
      campaignId: campaign.id,
      onProgress,
    })

    setState(state => ({
      ...state,
      missions: [...state.missions, ...allMissions],
    }))
    props.refetchMissions()
  }

  const validateRow = dto =>
    validateCreateMissionDto(dto, {
      locations: cityLocations,
      dateRange: [startDate, endDate],
      now: new Date(),
    })

  const downloadCSV = () =>
    downloadSampleCSV({
      locations: cityLocations,
      dateRange: [startDate, endDate],
    })

  const onImportSuccess = async (data, onProgress) => {
    await onMissionsImport(data, onProgress)
    setShowImportMissionsModal(false)
  }

  // Get page's title
  const pageTitle = getRosterTitle(week)
  // Figure out if user should be able to import missions
  const shouldDisplayImportButton = canImport({ week, year })
  // Get details rows that we want to render
  const detailsItemsData = getDetailsItems({
    missions,
    week,
    year,
  })
  // Get breadcrumbs items that we want to render
  const breadcrumbsData = getBreadcrumbs({
    campaign: campaign,
    cityPlanId: props.match.params.cityPlanId,
    campaignId: props.match.params.campaignId,
    fromCityScreen: props.location.fromCityScreen,
    city: city,
  })

  const onDelete = useCallback(async () => {
    setEditMode(false)
    const deletedIds = Object.keys(selectedMissionsMapRef.current).filter(
      id => selectedMissionsMapRef.current[id],
    )
    const payload = {
      unbookReason: UNBOOK_REASONS.CLIENT_REQUEST,
      missions: deletedIds.map(id => ({ id })),
    }

    setState(state => ({
      ...state,
      missions: state.missions.filter(({ id }) => !deletedIds.includes(id)),
    }))

    setSelectedMissionsMap({})
    setEditedMissionsData({})
    await apiClient.deleteMissions(payload)
  }, [])

  const onEdit = useCallback(
    (missionId, payload, duplicateToSelected) => {
      const newData = {
        ...payload,
        id: missionId,
      }
      if (newData.user && newData.user.id && availabilities) {
        handlingErrorContractAvailabilitiesPopup({
          userId: newData.user.id,
          availabilities,
          mission: missions.find(mission => mission.id === missionId),
          brandAmbassadors: [],
          dispatch,
        })
      }
      setEditedMissionsData(data => {
        let additionalData = {}
        if (duplicateToSelected && selectedMissionsMapRef.current[missionId]) {
          Object.keys(selectedMissionsMapRef.current).forEach(
            selectedMissionId => {
              if (selectedMissionsMapRef.current[selectedMissionId]) {
                additionalData[selectedMissionId] = {
                  ...(data[selectedMissionId] || {}),
                  ...newData,
                  id: selectedMissionId,
                }
              }
            },
          )
        }

        return {
          ...data,
          ...additionalData,
          [missionId]: {
            ...(data[missionId] || {}),
            ...newData,
          },
        }
      })
    },
    [availabilities && Object.keys(availabilities).length],
  )

  const onSave = async () => {
    runSnackBar({
      type: 'info',
      msg: 'Update in progress...',
      autoHideDuration: null,
    })
    try {
      const editedMissionsDataArr = Object.values(editedMissionsData)

      const hasNotValidBonusPerConversion = editedMissionsDataArr.some(
        m =>
          m.bonusPerConversion &&
          !isValidBonusPerConversion(m.bonusPerConversion),
      )

      if (hasNotValidBonusPerConversion) {
        runSnackBar({
          type: 'error',
          msg: 'Validation error...',
          autoHideDuration: 2,
        })
        return dispatch(
          showReduxModal({
            title: 'Validation error',
            children: BONUS_PER_CONVERSION_ERROR,
            confirmLabel: `OK`,
            hideCancel: true,
          }),
        )
      }

      const hasNotValidBonusThreshold = editedMissionsDataArr.some(
        m => m.bonusThreshold && !isValidPercent(m.bonusThreshold),
      )

      if (hasNotValidBonusThreshold) {
        runSnackBar({
          type: 'error',
          msg: 'Validation error...',
          autoHideDuration: 2,
        })
        return dispatch(
          showReduxModal({
            title: 'Validation error',
            children: BONUS_THRESHOLD_ERROR,
            confirmLabel: `OK`,
            hideCancel: true,
          }),
        )
      }
      setEditMode(false)

      for (let index = 0; index < editedMissionsDataArr.length; index++) {
        let newData = editedMissionsDataArr[index]
        if (newData.user) {
          newData = {
            ...newData,
            userId: newData.user.id,
            pickupTime: null,
            user: undefined,
          }
        }
        if (newData.draftPayAdjustment) {
          newData.draftPayAdjustment = Number(newData.draftPayAdjustment)
        }
        if (newData.bonusPerConversion || newData.bonusPerConversion === '') {
          newData.bonusOnConversion = Number(newData.bonusPerConversion)
          delete newData.bonusPerConversion
        }

        newData.bonusThreshold = newData.bonusThreshold
          ? Number(newData.bonusThreshold)
          : newData.bonusThreshold == ''
          ? null
          : undefined

        await updateMissionSimplified({ missionInput: newData })
      }

      runSnackBar({
        type: 'success',
        msg: 'All missions sucessfully updated!',
      })

      setEditedMissionsData({})
      setSelectedMissionsMap({})

      props.refetchMissions()
    } catch (error) {
      setEditMode(false)
      return runSnackBar({
        type: 'error',
        msg: error.message,
      })
    }
  }
  const onCheckbox = missionId =>
    setSelectedMissionsMap(map => ({ ...map, [missionId]: !map[missionId] }))

  const onGenerate = async missions => {
    if (missions.length) {
      setState(state => ({
        ...state,
        missions: [...state.missions, ...missions],
      }))
    }
  }

  const handleClickGenerateMissions = () => {
    if (campaign.isSelfServe) {
      handleConfirmationDialog({
        title: 'Restricted!',
        contentText: `You cannot manually generate missions for self-serve campaigns.`,
        setConfirmDialogData,
        rightButtonLable: 'OK',
      })
    } else {
      setIsModalOpen(true)
    }
  }

  const inNeedToShowOldGenerateMissionsButton = false

  return (
    <HideWhenBlocked>
      <RosterViewWrapper>
        <LayoutDashboard>
          {confirmDialogData.isOpen ? (
            <ConfirmDialog {...confirmDialogData} />
          ) : null}
          {isModalOpen ? (
            <GenerateMissionsModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onGenerate={onGenerate}
              campaign={campaign}
              city={city}
              cityPlan={cityPlan}
              dateRange={{ startDate, endDate }}
              onRefetch={() => {
                props.refetchMissions()
              }}
            />
          ) : null}
          <Modal show={showImportMissionsModal}>
            <ImportMissions
              onClose={() => setShowImportMissionsModal(false)}
              onImportSuccess={onImportSuccess}
              validateRow={validateRow}
              downloadSampleCSV={downloadCSV}
              isMasterSchedule={false}
            />
          </Modal>
          <BreadcrumbsRenderer
            icon={breadcrumbsData.icon}
            items={breadcrumbsData.items}
          />
          <PageTitle>{pageTitle}</PageTitle>
          {!authorization.isSpy() && (
            <MissionsControls
              missions={missions}
              campaign={campaign}
              updateData={() => props.refetchMissions()}
              isTeamLeaderEnabled={city.teamLeaderEnabled}
            />
          )}
          <DetailsSection items={detailsItemsData} />
          <section style={buttonsContainerStyle}>
            <ImportButton
              hidden={
                !shouldDisplayImportButton ||
                campaign.isSelfServe ||
                campaign.type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL
              }
              onClick={() => setShowImportMissionsModal(true)}
            />
            {campaign.type === CAMPAIGN_ENUM_TYPES.HANDTOHAND &&
              countriesShowPayroll.includes(countryCode) && (
                <ExportRoster
                  campaignId={campaign.id}
                  cityId={city.id}
                  startDate={startDate}
                  endDate={endDate}
                  filename={`${campaign.name} - ${pageTitle} - ${year}`}
                />
              )}
          </section>
          <MissionsOverview
            key="rosterMissionsOverview"
            rosters={[{ weekNum: week, missions }]}
            city={city}
          />
          <RosterMissionsTable
            {...props}
            {...state}
            onDelete={onDelete}
            onEdit={onEdit}
            selectedMissionsMap={selectedMissionsMap}
            onCheckbox={onCheckbox}
            editedMissionsData={editedMissionsData}
            onSave={onSave}
            isEditMode={isEditMode}
            setEditMode={setEditMode}
            generateMissionsButton={
              inNeedToShowOldGenerateMissionsButton ? (
                <GenerateButton onClick={handleClickGenerateMissions} />
              ) : null
            }
          />
          {props.city.country.code === 'DE' ? (
            <Availabilities
              cityId={props.city.id}
              week={Number(week)}
              callbackBrandAmbassadors={callbackBrandAmbassadors}
            />
          ) : null}
        </LayoutDashboard>
      </RosterViewWrapper>
    </HideWhenBlocked>
  )
}

const mapDispatchToProps = {
  showReduxModal,
  hideReduxModal,
}
const mapStateToProps = state => ({
  dayPeriodFilter: state.missions.missionsDayPeriodFilter,
  sortBy: state.missions.rosterViewMissionsSortBy,
  statusFilter: state.missions.missionsStatusFilter,
  typeFilter: state.missions.missionsTypeFilter,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RosterView)

const buttonsContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}
